import { getAccessToken } from '../utils/storage';

export interface PromptHistoryItem {
  id: string;
  query: string;
  timestamp: Date;
  sessionId?: string;
}

interface ApiMessage {
  id: string;
  message: string;
  created_at: string;
  session_id: string;
  user_id: string;
}

interface ApiResponse {
  messages: ApiMessage[];
  total: number;
  limit: number;
  offset: number;
}

class PromptHistoryService {
  private static readonly STORAGE_KEY = 'prompt_history';
  private static readonly MAX_HISTORY_ITEMS = 50;

  // Get all prompt history from API
  static async getHistory(userId: string, limit: number = 20, offset: number = 0): Promise<PromptHistoryItem[]> {
    try {
      // Try API first
      const apiHistory = await this.getHistoryFromAPI(userId, limit, offset);
      if (apiHistory.length > 0) {
        return apiHistory;
      }
      
      // Fallback to local storage if API fails
      return this.getLocalHistory();
    } catch (error) {
      console.error('Error loading prompt history from API, falling back to local storage:', error);
      return this.getLocalHistory();
    }
  }

  // Get history from API
  private static async getHistoryFromAPI(userId: string, limit: number = 20, offset: number = 0): Promise<PromptHistoryItem[]> {
    const apiUrl = process.env.REACT_APP_API_URL;
    const accessToken = getAccessToken();

    if (!apiUrl) {
      throw new Error('API URL is not configured');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${apiUrl}/v1/sessions/user/${userId}/messages?limit=${limit}&offset=${offset}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ApiResponse = await response.json();
    
    // Validate the response structure
    if (!data.messages || !Array.isArray(data.messages)) {
      console.warn('API response missing messages array:', data);
      throw new Error('Invalid API response format');
    }
    
    return data.messages
      .filter((message: ApiMessage) => message && message.message && message.id)
      .map((message: ApiMessage) => ({
        id: message.id,
        query: message.message,
        timestamp: new Date(message.created_at),
        sessionId: message.session_id
      }))
      .sort((a: PromptHistoryItem, b: PromptHistoryItem) => 
        b.timestamp.getTime() - a.timestamp.getTime()
      );
  }

  // Get local storage history (fallback)
  private static getLocalHistory(): PromptHistoryItem[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      
      const parsed = JSON.parse(stored);
      return parsed.map((item: any) => ({
        ...item,
        timestamp: new Date(item.timestamp)
      })).sort((a: PromptHistoryItem, b: PromptHistoryItem) => 
        b.timestamp.getTime() - a.timestamp.getTime()
      );
    } catch (error) {
      console.error('Error loading local prompt history:', error);
      return [];
    }
  }

  // Add a new prompt to history (local storage only, API handles server-side history)
  static addToHistory(query: string, sessionId?: string): void {
    try {
      const history = this.getLocalHistory();
      
      // Avoid duplicating the same query if it's the most recent one
      if (history.length > 0 && history[0].query === query) {
        return;
      }

      const newItem: PromptHistoryItem = {
        id: Date.now().toString(),
        query: query.trim(),
        timestamp: new Date(),
        sessionId
      };

      const updatedHistory = [newItem, ...history].slice(0, this.MAX_HISTORY_ITEMS);
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedHistory));
    } catch (error) {
      console.error('Error saving prompt to history:', error);
    }
  }

  // Clear all history (only local storage, API history is managed server-side)
  static clearHistory(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing prompt history:', error);
    }
  }

  // Remove a specific item from history (only local storage)
  static removeFromHistory(id: string): void {
    try {
      const history = this.getLocalHistory();
      const filteredHistory = history.filter(item => item.id !== id);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredHistory));
    } catch (error) {
      console.error('Error removing item from history:', error);
    }
  }

  // Get recent history (last N items) - async version
  static async getRecentHistory(userId: string, count: number = 10): Promise<PromptHistoryItem[]> {
    const history = await this.getHistory(userId, count, 0);
    return history.slice(0, count);
  }

  // Get recent history synchronously from local storage (fallback)
  static getRecentHistorySync(count: number = 10): PromptHistoryItem[] {
    return this.getLocalHistory().slice(0, count);
  }
}

export default PromptHistoryService;